'use client';

import type { FC } from 'react';

import { useEffect, useState } from 'react';

import clsx from 'clsx';
import parse from 'html-react-parser';

import type { Article, ArticlePreview } from '@hyex/dxp/types/blog/Article';
import type { BlogCategory } from '@hyex/dxp/types/blog/Category';
import useIsMobile from '@hyex/utils/hooks/useIsMobile';

import { NO_ARTICLES } from '@hyex/modules/portal/constants';
import SmartLink from '../base/Link';
import CategoryArticleList from './category-article-list';

type PropTypes = {
  items: Article[] | ArticlePreview[];
  category: BlogCategory | null;
  categoryName?: string;
  categoryPath?: string;
  articlePath?: string;
  basePath: string;
  prefetchLinks?: boolean;
  isSingleCategoryPage?: boolean;
};

const ArticlesCategorySection: FC<PropTypes> = ({
  items,
  categoryName,
  category,
  categoryPath,
  articlePath,
  basePath,
  prefetchLinks = true,
  isSingleCategoryPage = false,
}) => {
  const [isMounted, setMounted] = useState(false);
  const [hash, setHash] = useState('');

  useEffect(() => {
    if (isMounted) {
      setHash(window.location.hash);
    } else {
      setMounted(true);
    }
  }, [isMounted]);

  useEffect(() => {
    const updateHash = () => {
      setHash(window.location.hash);
    };

    window.addEventListener('hashchange', updateHash);
    window.addEventListener('popstate', updateHash); // Handles history navigation

    return () => {
      window.removeEventListener('hashchange', updateHash);
      window.removeEventListener('popstate', updateHash);
    };
  }, []);

  const isMobile = useIsMobile(821);

  const name = categoryName ? categoryName : category ? category.name : '';

  const categoryFullPath = `${categoryPath}${category?.value}`;

  const SubcategoryHeading = isSingleCategoryPage ? 'h2' : 'h3';

  const renderSubcategories = (subCat: BlogCategory, subcatItems: (Article | ArticlePreview)[]) => {
    const layout = (
      <>
        {subCat?.name && (
          <SubcategoryHeading className={isSingleCategoryPage ? 'smaller' : 'title'} id={subCat?.value}>
            {subCat?.name}
          </SubcategoryHeading>
        )}
        {subCat?.description && <p className="smaller">{parse(subCat?.description ?? '')}</p>}
        {subcatItems && subcatItems.length === 0 ? (
          <p className="subtitle">{NO_ARTICLES}</p>
        ) : (
          <CategoryArticleList
            items={subcatItems}
            categoryPath={categoryPath}
            articlePath={articlePath}
            prefetchLinks={prefetchLinks}
          />
        )}
      </>
    );

    return layout;
  };

  const CategoryHeading = isSingleCategoryPage ? 'h1' : 'h2';

  const ExploreHeading = isSingleCategoryPage ? 'h2' : 'h3';

  return (
    <section className="category-section">
      <div className="container">
        <div className="category-title">
          <CategoryHeading className="title">{name}</CategoryHeading>

          {category ? (
            <>
              <SmartLink
                category={{
                  path: categoryFullPath,
                }}
                isMobile={isMobile}
                entityName="Articles"
              />
            </>
          ) : (
            <SmartLink
              category={{
                path: basePath,
              }}
              isMobile={isMobile}
              entityName="Articles"
            />
          )}
        </div>
        {category?.description && <p className="bigger">{parse(category?.description ?? '')}</p>}
        <ExploreHeading className={isSingleCategoryPage ? 'subcategories smaller' : 'subcategories'}>Explore {name}</ExploreHeading>
        {category?.subcategoriesCompleted && category?.subcategoriesCompleted.length > 0 ? (
          <>
            <div className="subcategories-nav">
              {category?.subcategoriesCompleted?.map((subCat) => {
                const subcatItems = items.filter(
                  (el: Article | ArticlePreview) => el.category?.id === subCat.id
                );

                return subcatItems && subcatItems.length > 0 ? (
                  <div className="subcategories-nav-item" key={subCat.id}>
                    <a
                      className={clsx('subcategories-nav-link', {
                        active: hash && hash === `#${subCat.value}`,
                      })}
                      href={`#${subCat.value}`}
                    >
                      {subCat.name}
                    </a>
                  </div>
                ) : null;
              })}
            </div>
            {category?.subcategoriesCompleted?.map((subCat) => {
              const subcatItems = items.filter(
                (el: Article | ArticlePreview) => el.category?.id === subCat.id
              );

              return subcatItems && subcatItems.length > 0
                ? renderSubcategories(subCat, subcatItems)
                : null;
            })}
          </>
        ) : (
          <CategoryArticleList
            items={items}
            categoryPath={categoryPath}
            articlePath={articlePath}
            prefetchLinks={prefetchLinks}
          />
        )}
      </div>
    </section>
  );
};

export default ArticlesCategorySection;
